// User-record contract types — moved verbatim from `src/types/index.ts` and
// `src/types/patient-summary.types.ts` (which re-export from here). These are
// pure type-expressions over `@heidi/schema` plus temporary FE extensions,
// each tagged with its schema-regen TODO exactly as at the old path. The user
// record is a cross-domain aggregate: per-domain extension fields (growth's
// post-note-quality markers, session settings, dictation) ride the user noun,
// whose contract home is workspace.

import { type components } from '@heidi/schema/ml-scribe';
import {
  type DictationSettingsRequestWithCursorAwareness,
  type DictationSettingsResponseWithCursorAwareness,
} from './dictation-cursor-awareness.types';

export type EffectiveSessionTimeout = components['schemas']['EffectiveSessionTimeout'];

export type SessionSettingsResponseWithHighlights =
  components['schemas']['SessionSettingsResponse'] & {
    show_verified_highlights?: boolean;
    show_issue_highlights?: boolean;
    personalization_opted_in?: boolean;
    /**
     * Per-user Heidi Labs opt-ins. Always an object on read (empty = `{}`).
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    labs_feature_overrides?: { [key: string]: boolean };
    /**
     * Default font + size for "Export as PDF/Word" (see request type above).
     * TODO: Remove extension after BE adds the fields + pnpm schema is re-run.
     */
    default_export_font?: string | null;
    default_export_font_size?: number | null;
    /**
     * Account-level identify-speakers preference, applied on every session.
     * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
     */
    identify_speakers?: boolean | null;
    /**
     * Account-level detect-roles preference, applied on every session.
     * Absent or null means the preference is on.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    detect_roles?: boolean | null;
    /**
     * Default font size for newly detected form fields. `0` = Auto, `null` =
     * PDF Default, positive values are pt sizes. When unset, the FE leaves PDF Default.
     * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
     */
    default_form_field_font_size?: number | null;
    /**
     * INT-5188: when true, the org has disabled the "Remember consent for
     * future sessions" checkbox across every consent surface.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    hide_remember_consent_checkbox?: boolean | null;
    /**
     * Individual chat retention days (null = not set).
     * TODO: Remove extension after pnpm schema is re-run against deployed BE (EVI-1453).
     */
    chat_retention_days?: number | null;
    /**
     * True when the chat retention value has been locked to the team's policy.
     * When true, the UI toggle and input are disabled.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE (EVI-1453).
     */
    chat_retention_managed_by_team?: boolean;
    /**
     * Auto-download generated form fills after successful document creation.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    auto_download_filled_forms?: boolean;
    /**
     * Whether the V2 session canvas uses its constrained reading width.
     * Optional while older backend versions omit the preference.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    is_readable_width_enabled?: boolean | null;
    /**
     * Connected apps stack for the Work agent (nango | merge). Exclusive.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    connected_apps_provider?: 'nango' | 'merge';
  };

/**
 * Tracks how many *distinct* sessions the user dismissed the queued patient summary
 * in, the last such session (for same-session dedupe), and whether the prompt has
 * already been shown/answered (so it only ever appears once per user).
 *
 * Lives on the user object as a top-level `patient_summary_turn_off_prompt` field
 * (schema regen pending — see the `UserResponse` / `UpdateUserRequest` extensions in
 * `src/types/index.ts`).
 */
export interface PatientSummaryTurnOffPromptState {
  /** Number of distinct sessions the patient summary was dismissed in. */
  dismissal_count: number;
  /** Session id of the most recent dismissal, used to dedupe same-session repeats. */
  last_dismissed_session_id: string | null;
  /** True once the prompt has been shown and answered (turn off / keep on / close). */
  seen: boolean;
}

export type UserResponse = Omit<
  components['schemas']['UserResponse'],
  'session_settings' | 'dictation'
> & {
  session_settings?: SessionSettingsResponseWithHighlights | null;
  /**
   * Heidi Verify auto-mode preference. When true, entity extraction + verification
   * run automatically in parallel with note generation once the user finishes a session.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  is_auto_verify_enabled?: boolean;
  /**
   * Durable marker: true once the auto-verify preference has been deliberately
   * set (by the AUTO_K one-time auto-enable or a manual toggle). Gates the
   * one-time auto-enable so an explicit opt-out is never overridden.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  auto_verify_preference_set?: boolean;
  /** Present on GET user once a sales-booking form has been submitted (see OpenAPI UserResponse). */
  sales_booking_forms_submitted?: {
    [key: string]: components['schemas']['SalesBookingFormSubmission'];
  };
  dictation?: DictationSettingsResponseWithCursorAwareness | null;
  /** See {@link EffectiveSessionTimeout}. Optional/null pending PR-2 deploy + schema regen. */
  session_timeout?: EffectiveSessionTimeout | null;
  /**
   * GRO-3270: completed task ids for the post-note quality "Improve your
   * notes" panel. Values match `ImproveNotesTaskId` in
   * `src/features/post-note-quality/improve-notes-panel/tasks.config.ts`.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  post_note_quality_completed_tasks?: string[];
  /**
   * GRO-3270: set when the user permanently dismisses the post-note quality
   * panel from its all-complete state; the panel and launcher never render
   * again once present.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  post_note_quality_panel_dismissed_at?: string | null;
  /**
   * GRO-3284: sidebar task-count milestones (2, 5) for which the +7 trial-day
   * reward has been granted (idempotency marker; backend-owned).
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  post_note_quality_reward_milestones_granted?: number[];
  /**
   * GRO-3284: true once the user has seen the post-note quality "Improve your
   * notes" panel at least once — the source of truth for the `first_view`
   * analytics prop on `panel_opened`. Backend-owned; set on first view.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  has_seen_improve_notes_panel?: boolean;
  /**
   * State for the "keep automatic patient summaries?" turn-off prompt. Tracks
   * distinct-session dismissals of the queued patient summary and whether the
   * prompt has been shown. Optional/nullable pending BE merge + schema regen.
   */
  patient_summary_turn_off_prompt?: PatientSummaryTurnOffPromptState | null;
  /**
   * Personal workspace id, present on the get-or-create response when a
   * workspace is provisioned for a new user. Lets onboarding scope the
   * immediate profile update to the just-created workspace.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  workspace_id?: string | null;
  /**
   * Read-only auto-enrolment stamp, backend-owned. `true` once the user was
   * auto-enrolled into V2 at account creation. The render hook treats this OR
   * the Labs `session_layout_v2` override as "show V2", and hides the Labs
   * toggle when it is true. Never written from the FE.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  scribe_session_page_v2_enabled?: boolean;
  /**
   * ISO-8601 acceptance stamp for the Labs Experimental Features Supplemental
   * Terms. Null/absent means never accepted.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  labs_experimental_terms_agreed_on?: string | null;
  /**
   * ISO-8601 stamp set when the /auth/onboard page created the user before
   * the in-app steps ran; cleared by `onboarding_completed` on PATCH /user.
   * Null/absent means there is no in-app onboarding left to resume.
   * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
   */
  onboarding_pending_at?: string | null;
};

// --- IP2 (ARCFE-956): user-projection API contracts -------------------------

/** Moved verbatim from `src/api/scribeAPIV2.ts:2080` (which re-exports). */
export type SnippetsResponse = components['schemas']['SnippetsResponse'];

/** Moved from `src/api/scribeAPIV2.ts` with the bulk snippet import (which re-exports). */
export type VerbalTriggerIntent = { enabled: true; phrase: string } | { enabled: false };

export interface UploadRow {
  row: number;
  msg: BulkUploadSnippetErrorType;
  data: components['schemas']['SnippetView'];
}

export enum BulkUploadSnippetErrorType {
  FILE_TOO_LARGE = 'fileTooLarge',
  UNSUPPORTED_FILE_FORMAT = 'unsupportedFileFormat',
  CSV_PARSING_ERROR = 'csvParsingError',
  MISSING_REQUIRED_COLUMNS = 'missingRequiredColumns',
  NO_VALID_ROWS_FOUND = 'noValidRowsFound',
  DUPLICATE_KEY = 'duplicateKey',
  INVALID_CHARS = 'invalidChars',
  KEY_ALREADY_EXISTS = 'keyAlreadyExists',
}

export interface SnippetsUploadResponse {
  valid_rows: UploadRow[];
  error_rows: UploadRow[];
}

/** Moved verbatim from `src/types/index.ts` (which re-exports). */
export type PersonalizationDataResponse =
  components['schemas']['PersonalizationDataResponse'];

// --- D1 (ARCFE-1037): user-record WRITE contracts ---------------------------
// Request-side siblings of the response extensions above, moved verbatim from
// `src/types/index.ts` (which re-exports) with the eight user-write endpoint
// carve. Same schema-regen TODO discipline: every FE extension is temporary.

// Extend UpdateUserRequest to include entity verification highlight preferences.
// TODO: Remove extension after BE PR #6424 merges and pnpm schema is re-run
// Also bridges `personalization_opted_in` (BE has shipped it; FE schema regen
// against deployed BE is held off until an unrelated Gender enum rename can
// be handled in the consumers it touches).
// TODO: Remove `personalization_opted_in` once pnpm schema is re-run.
export type SessionSettingsRequestWithHighlights =
  components['schemas']['SessionSettingsRequest'] & {
    show_verified_highlights?: boolean | null;
    show_issue_highlights?: boolean | null;
    personalization_opted_in?: boolean | null;
    /**
     * Per-user Heidi Labs opt-ins (tri-state map: present=forced on/off,
     * absent=follow the flag). Free-form keys; full-object replace on write.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    labs_feature_overrides?: { [key: string]: boolean } | null;
    /**
     * Default font + size for "Export as PDF/Word". `default_export_font` is
     * `ExportFontValue` from `@heidi/session` `exportFonts.ts`.
     * TODO: Remove extension after BE adds the fields + pnpm schema is re-run.
     */
    default_export_font?: string | null;
    default_export_font_size?: number | null;
    /**
     * Account-level identify-speakers preference, applied on every session.
     * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
     */
    identify_speakers?: boolean | null;
    /**
     * Account-level detect-roles preference, applied on every session.
     * Absent or null means the preference is on.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    detect_roles?: boolean | null;
    /**
     * Default font size for newly detected form fields. `0` = Auto, `null` =
     * PDF Default, positive values are pt sizes. When unset, the FE leaves PDF Default.
     * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
     */
    default_form_field_font_size?: number | null;
    /**
     * Individual chat retention: days after which evidence chats are deleted.
     * Null = no retention policy set by the user (team policy may still apply).
     * TODO: Remove extension after pnpm schema is re-run against deployed BE (EVI-1453).
     */
    chat_retention_days?: number | null;
    /**
     * Auto-download generated form fills after successful document creation.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    auto_download_filled_forms?: boolean | null;
    /**
     * Whether the V2 session canvas uses its constrained reading width.
     * TODO: Remove extension after pnpm schema is re-run against deployed BE.
     */
    is_readable_width_enabled?: boolean | null;
  };

export type UpdateUserRequest = Omit<
  components['schemas']['UpdateUserRequest'],
  'session_settings' | 'dictation'
> & {
  session_settings?: SessionSettingsRequestWithHighlights | null;
  /**
   * Heidi Verify auto-mode preference.
   * TODO: Remove extension after pnpm schema is re-run against deployed BE.
   */
  is_auto_verify_enabled?: boolean | null;
  /** Durable marker gating the one-time auto-enable; see the UserResponse extension above. */
  auto_verify_preference_set?: boolean | null;
  dictation?: DictationSettingsRequestWithCursorAwareness | null;
  /**
   * Turn-off-prompt state for automatic patient summaries. Written as a top-level
   * user field (PATCH /user), not via `session_settings`. Optional/nullable pending
   * BE merge + schema regen.
   */
  patient_summary_turn_off_prompt?: PatientSummaryTurnOffPromptState | null;
  /**
   * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
   */
  labs_experimental_terms_agreed_on?: string | null;
  /**
   * `true` clears `onboarding_pending_at` once the in-app onboarding finishes.
   * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
   */
  onboarding_completed?: boolean | null;
};

/** Moved verbatim from `src/types/index.ts` (which re-exports). */
export type CreateUserRequest = components['schemas']['CreateUserRequest'];

/** Moved verbatim from `src/types/index.ts` (which re-exports). */
export type SelfOnboardRequest = components['schemas']['SelfOnboardRequest'];

/** Moved verbatim from `src/types/index.ts` (which re-exports). */
export type SelfOnboardResponse = components['schemas']['SelfOnboardResponse'];

/**
 * Wire shape of PATCH /user/onboarding-subtasks — the schema's own
 * `PendingSubStep` component. Structurally identical to the app's
 * hand-rolled `PendingSubStep` in
 * `src/hooks/mutation/useMutateUserOnboardingSubTask.ts` (`step` is the
 * `TutorialSteps` enum, `page` the union of the five page enums), so callers
 * pass their existing values unchanged; the schema component is authoritative
 * here so the contract cannot drift from the wire.
 */
export type PendingSubStep = components['schemas']['PendingSubStep'];

/**
 * The two notification names the notifications settings form addresses.
 *
 * Moved from `src/types/index.ts` (which re-exports) WITH ITS ONLY CONSUMER,
 * the notifications form hook now in this package's `/ui/settings`. It is not
 * taken from a `@heidi/schema` door because there is none to take: the wire
 * types this shape reads (`UserNotificationPreference.name`) are generated as
 * a bare `string`, so the vocabulary is the FE's until the backend enumerates
 * it.
 */
export enum NotificationName {
  SESSION_EXPIRING = 'session_expiring',
  PATIENT_EXPIRING = 'patient_expiring',
}

export type WordLibrary =
  | {
      [key: string]: string;
    }
  | null
  | undefined;
